// Gemini Text Service — calls Google AI direct via server proxy for credit tracking.
// Models: gemini-3.5-flash, gemini-3.1-pro-preview, gemini-3-flash-preview, gemini-3.1-flash-lite.

import logger from '../utils/logger';
import { supabase } from '../lib/supabase';
import { optimizeImage, formatFileSize } from '../utils/imageOptimization';
import type { ChatMessage, ConversationContext } from '../types/api';

interface GeminiTextResponse {
  content: string;
  context: ConversationContext;
  type?: 'text';
}

class GeminiTextService {
  private apiBase: string;

  constructor() {
    this.apiBase = process.env.REACT_APP_API_BASE
      || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
    logger.info('[GeminiTextService] Initialized - all requests go through server proxy at', this.apiBase);
  }

  isConfigured(): boolean {
    return typeof window !== 'undefined';
  }

  /** Convert any image URL to optimized base64 data URL */
  private async toBase64DataUrl(imageUrl: string): Promise<string | null> {
    try {
      const result = await optimizeImage(imageUrl, {
        maxDimension: 2048,
        maxFileSizeMB: 4,
        preserveTransparency: false,
      });
      if (result.wasOptimized) {
        logger.info(`[GeminiTextService] Image optimized: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.optimizedSize)}`);
      }
      return result.dataUrl;
    } catch (e) {
      logger.warn('[GeminiTextService] Failed to optimize image, attempting raw conversion:', e);
      try {
        if (imageUrl.startsWith('data:')) return imageUrl;
        if (imageUrl.startsWith('blob:') || imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const base64Data: string = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (typeof reader.result === 'string') resolve(reader.result);
              else reject(new Error('FileReader result is not a string'));
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(blob);
          });
          return base64Data;
        }
        return null;
      } catch (fetchErr) {
        logger.warn('[GeminiTextService] Failed to fetch image for conversion:', imageUrl, fetchErr);
        return null;
      }
    }
  }

  /** Streaming response with token callbacks */
  async generateResponseStream(
    prompt: string,
    systemPrompt: string | null = null,
    context: ConversationContext | null = null,
    onToken?: (delta: string) => void,
    model: string = 'gemini-3.5-flash',
    imageUrls?: string[]
  ): Promise<GeminiTextResponse> {
    const { data: { user } } = await supabase.auth.getUser();

    // Build context messages — convert any embedded image parts to base64
    const contextMessages: ChatMessage[] = [];
    if (context?.messages) {
      for (const msg of context.messages) {
        if (typeof msg.content === 'string') {
          contextMessages.push(msg);
        } else if (Array.isArray(msg.content)) {
          const processedContent: any[] = [];
          for (const part of msg.content) {
            if (part.type === 'text') {
              processedContent.push(part);
            } else if (part.type === 'image' && part.imageUrl) {
              const base64 = await this.toBase64DataUrl(part.imageUrl);
              if (base64) {
                processedContent.push({ type: 'image', imageUrl: base64 });
              }
            }
          }
          if (processedContent.length > 0) {
            contextMessages.push({ role: msg.role, content: processedContent });
          }
        }
      }
    }

    // Process direct image URLs (from upstream nodes) to base64
    const processedImageUrls: string[] = [];
    if (imageUrls && imageUrls.length > 0) {
      for (const url of imageUrls) {
        const base64 = await this.toBase64DataUrl(url);
        if (base64) {
          processedImageUrls.push(base64);
          logger.info('[GeminiTextService] Processed image for vision input');
        }
      }
    }

    const streamHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (user?.id) {
      streamHeaders['x-user-id'] = user.id;
    }

    const res = await fetch(`${this.apiBase}/api/googleai/text/stream`, {
      method: 'POST',
      headers: streamHeaders,
      body: JSON.stringify({
        prompt,
        systemPrompt,
        model,
        imageUrls: processedImageUrls.length > 0 ? processedImageUrls : undefined,
        context: { messages: contextMessages },
      }),
    });

    if (!res.ok) {
      const errorBody = await res.text();
      logger.error('[GeminiTextService] Stream request failed:', res.status, errorBody);
      throw new Error(`Gemini Text API error: ${res.status} - ${errorBody}`);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);

        if (!raw || raw.startsWith(':')) continue;

        const lines = raw.split('\n');
        let event: string | null = null;
        let dataStr = '';

        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }

        if (!dataStr) continue;

        try {
          const payload = JSON.parse(dataStr);
          if (event === 'done' && typeof payload.content === 'string') {
            fullContent = payload.content;
          } else if (event === 'error') {
            throw new Error(payload.message || 'Gemini Text stream error');
          } else if (typeof payload.delta === 'string') {
            fullContent += payload.delta;
            onToken?.(payload.delta);
          }
        } catch {
          // Ignore malformed chunks
        }
      }
    }

    const allMessages: ChatMessage[] = [
      ...contextMessages,
      { role: 'user', content: prompt },
      { role: 'assistant', content: fullContent },
    ];

    return {
      content: fullContent,
      context: { messages: allMessages },
      type: 'text',
    };
  }
}

const geminiTextService = new GeminiTextService();
export default geminiTextService;
